import { createSlice } from '@reduxjs/toolkit'
import { v4 as uuidv4 } from 'uuid';
import Cookies from 'js-cookie';
import { fetchWrapper } from '../../helper/utils'
import { setError } from '../error/errorSlice'
import { setLoader } from '../loader/loaderSlice'
import { setAmplitudeUserProperties } from '../../analytics/amplitude'
import { setAnalyticsUserId } from '../../analytics/common'
import { setEventData } from '../events/eventsSlice';


const {
  REACT_APP_API_URL,
  REACT_APP_STRIPE_PK,
  REACT_APP_STRIPE_NAME,
} = process.env;

export const signupSlice = createSlice({
    name: 'signup',
    initialState: {
        email: '',
        userName: '',
        isValid: false,
        token: null,
        userId: '',
        isSuccess: null,
        userUuid: null,
        stripePublishKey: REACT_APP_STRIPE_PK,
        stripeAccountName: REACT_APP_STRIPE_NAME,
        btToken: null,
        paymentProvider: null,
        deeplinkToken: null,
        paymentSettingsStatus: null,
        threeDS: 0,
    },
    reducers: {
        setEmail: (state, action) => {
            state.email = encodeURIComponent(action.payload.email);
            state.isValid = action.payload.isValid
        },
        setRequestResult: (state, action) => {
            const {
                access_token,
                entity: { external_user_id, name, registration_source, email },
            } = action.payload;
            state.token = access_token;
            state.isSuccess = true;
            state.userId = external_user_id;
            state.userName = name === email ? '' : name;
            state.email = encodeURIComponent(email);
            state.registrationSource = registration_source
        },
        setUserToken: (state, action) => {
            state.token = action.payload
        },
        setUserUuid: (state, action) => {
            state.userUuid = action.payload
        },
        setStripeData: (state, action) => {
            state.stripePublishKey = action.payload.stripePublishKey;
            state.stripeAccountName = action.payload.stripeAccountName
        },
        setBtToken: (state, action) => {
            state.btToken = action.payload;
        },
        setPaymentProvider: (state, action) => {
            state.paymentProvider = action.payload;
        },
        setDeeplinkToken: (state,action) => {
            state.deeplinkToken = action.payload
        },
        setPaymentSettingStatus: (state, action) => {
            state.paymentSettingsStatus = action.payload;
        },
        setThreeDS: (state, action) => {
            state.threeDS = action.payload;
        },
    },
});

export const {
    setEmail,
    setRequestResult,
    setUserToken,
    setUserUuid,
    setStripeData,
    setBtToken,
    setPaymentProvider,
    setDeeplinkToken,
    setPaymentSettingStatus,
    setThreeDS,
} = signupSlice.actions;

export const sendAPIRequest = (email, name, landingType, userUuid) => (dispatch) => {
    dispatch(setLoader(true));

    const params = {
        registration_source: 'email',
        name: name || email,
        email,
        landing_type: landingType,
        user_uuid: userUuid,
    };

    fetchWrapper(`${REACT_APP_API_URL}/signup`, { body: params })
        .then((data) => {
            window.sessionStorage.setItem('access_token', data.data.access_token);
            dispatch(setRequestResult(data.data));
            dispatch(setDeeplinkToken(data.data.deeplink_token));
            dispatch(getPaymentSettings(data.data.access_token));
            setAnalyticsUserId(data.data.entity.external_user_id);
        })
        .catch((error) => {
            dispatch(
                setError({
                    show: true,
                    text: error.errors?.messages || 'Failed to create an account',
                    type: 'signup',
                })
            )
        // dispatch(setRequestResult({isSuccess: false}));
        });
};

const getPaymentSettings = (token, attemptNum = 0) => (dispatch) => {
    let stripeParams = {
        stripePublishKey: REACT_APP_STRIPE_PK,
        stripeAccountName: REACT_APP_STRIPE_NAME,
      },
      status = null,
      paymentGateway = null;

    fetchWrapper(`${REACT_APP_API_URL}/users/me/payments-settings`, {headers: { Authorization: `Bearer ${token}` },})
        .then((data) => {
            const {
                stripe_publishable_key,
                stripe_account_name,
                braintree_client_token,
                payment_gateway,
                request_three_d_secure,
            } = data;
            paymentGateway = payment_gateway || 'stripe';
            dispatch(setBtToken(braintree_client_token || null));
            dispatch(setThreeDS(request_three_d_secure || 0));
            status = 'success';
            if (data.experiments?.force_3ds_for_eu_customers) {
                setAmplitudeUserProperties({ '3Ds_test': data.experiments?.force_3ds_for_eu_customers || null });
            }
            if (stripe_publishable_key) {
                stripeParams = {
                    stripePublishKey: stripe_publishable_key,
                    stripeAccountName: stripe_account_name,
                }
            }
        })
        .catch((error) => {
            console.log(error);
            if (attemptNum < 2) {
                setTimeout(() => dispatch(getPaymentSettings(token,++attemptNum)),1000);
            } else {
                status = 'fail';
                paymentGateway = 'stripe';
            }
        })
        .then(() => {
            dispatch(setPaymentProvider(paymentGateway));
            dispatch(setPaymentSettingStatus(status));
            dispatch(setStripeData(stripeParams));
        })
        .then(() => dispatch(setLoader(false)))
};

export const sendSignupTokenRequest =
	({ token, user_uuid, landing_type }) =>
	(dispatch) => {
		dispatch(setLoader(true));

		const params = {
			access_token: token,
			driver: 'google',
			landing_type,
			user_uuid,
		};

		fetchWrapper(`${REACT_APP_API_URL}/authorize_social_via_access_token`, {
			body: params,
		})
			.then((data) => {
				window.sessionStorage.setItem('access_token', data.data.access_token);
				dispatch(setRequestResult(data.data));
				dispatch(setDeeplinkToken(data.data.deeplink_token));
				dispatch(getPaymentSettings(data.data.access_token));
				setAnalyticsUserId(data.data.entity.external_user_id);
			})
			.catch((error) => {
				dispatch(
					setError({
						show: true,
						text: error.errors?.messages || 'Failed to create an account',
						type: 'signup',
					})
				);
				dispatch(setLoader(false));
			});
	};

export const setUuid = (userUuid) => (dispatch) => {
    const cookieUuid = Cookies.get('userUuid');

    if (!userUuid) {
        if (cookieUuid) {
            userUuid = cookieUuid;
        } else {
            userUuid = uuidv4();
        }
    }

    if (!cookieUuid) Cookies.set('userUuid', userUuid, { expires: 365 });
    dispatch(setUserUuid(userUuid));
    dispatch(setEventData({ id: 'userUuid', data: cookieUuid }));
    setAmplitudeUserProperties({ uuid: userUuid });
};

export const selectEmail = (state) => state.signup.email;
export const selectValidity = (state) => state.signup.isValid;
export const selectSuccess = (state) => state.signup.isSuccess;
export const selectToken = (state) => state.signup.token;
export const selectUserID = (state) => state.signup.userId;
export const selectUserName = (state) => state.signup.userName;
export const selectUserUuid = (state) => state.signup.userUuid;
export const selectStripePublishKey = (state) => state.signup.stripePublishKey;
export const selectStripeAccountName = (state) => state.signup.stripeAccountName;
export const selectBtToken = state => state.signup.btToken;
export const selectPaymentProvider = state => state.signup.paymentProvider;
export const selectDeeplinkToken = (state) => state.signup.deeplinkToken;
export const selectPaymentSettingsStatus = (state) => state.signup.paymentSettingsStatus;
export const selectThreeDS = (state) => state.signup.threeDS;

export default signupSlice.reducer
